import * as React from "react";
import {Button} from "@bizform/primereact/components/button/Button";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {ExceptionType} from "@bizform/type/model/model/Exception.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {ComponentHelper, Debug} from "@bizform/helper/dist/js/bizflow/helper.min";
import {
    BlockHandler,
    ComponentElementClass,
    ExceptionHandler,
    ReactComponentElementInit
} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../AbstractComponentElement";
import {I18n} from "../../misc/model/I18n.model";
import {SignatureHelper} from "./helper/Signature.helper";


type Props = {
};

type WrappedCompState = {
    value?: string;
    placeholder?: string;
    tabindex?: number;
    metaInfo?: string;
    signAttribute?: any;
    signStyle?: any;
    showClearIcon?: boolean;
    showSignIcon?: boolean;
    showResignIcon?: boolean;
    clearConfirm?: string;
    signConfirm?: string;
    resignConfirm?: string;
};

type WrappedFormState = {
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**npm
 * @since 0.0.1
 * @author Heungsoo Ham
 * @class BfSignatureE
 * @description BfSignature component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfSignatureE extends AbstractComponentElement<Props, State> {
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_p69DM6TK2rkfr7cPGEQlF1RXIXMZp7r",
        category: ComponentCategory.MISC,
        label: I18n.componentElement.eSignature.label,
        description: I18n.componentElement.eSignature.description,
        icon: "",
        dataType: [DataType.SIGN],
        property: null
    };

    private _init: boolean;

    focus(): void {
        if (this._button) {
            this._button.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(property, this);
            if (this._init) {
                newState.wrappedCompState.value = this.state.wrappedCompState.value;
            }
            this._init = true;
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: string): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: string): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: value && value.split ? value.split("@")[0] : value
            }
        };

        this.setState(newState);
    }

    @ReactComponentElementInit(BfSignatureE)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _button: any;
    private _signature: any;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            wrappedCompState: {
                value: null
            },
            wrappedFormState: {}
        };

        this._setRef = this._setRef.bind(this);
        this._setButtonRef = this._setButtonRef.bind(this);
        this._callSign = this._callSign.bind(this);
        this._callClearSign = this._callClearSign.bind(this);
        this._doSign = this._doSign.bind(this);
        this._doReSign = this._doReSign.bind(this);
        this._doClearSign = this._doClearSign.bind(this);
        this._doneReadSign = this._doneReadSign.bind(this);
    }

    componentDidUpdate(): void {
        if (this._button) {
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, this._button, this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    render(): React.ReactNode {
        if (!this.displayMode) {
            return null;
        } else {
            const ws = this.state.wrappedCompState;
            const parentHeight = ws.signStyle.height;
            const margin = ws.signStyle.margin ? ws.signStyle.margin : "auto";
            const image = ws.value ? <img ref={this._setRef} src={ws.value} style={{margin: margin, display: "block", opacity: "0"}} onLoad={SignatureHelper.adjustImageSize} /> : <div ref={this._setRef}>&nbsp;</div>;
            if (this.displayMode !== ComponentElementDisplayMode.EDIT) {
                return (
                    <div className="w-100 d-flex" style={{height: parentHeight + "px"}}>
                        {image}
                    </div>
                );
            } else {
                const sb = !ws.value && ws.showSignIcon ? <Button
                    key="_signButton"
                    icon="pi pi-pencil"
                    onClick={this._doSign}
                    style={{height: parentHeight + "px", minHeight: "30px", verticalAlign: "top"}}
                    title="sign"
                    ref={this._setButtonRef}
                /> : null;
                const rb = ws.value && ws.showResignIcon ? <Button
                    key="_reSignButton"
                    icon="pi pi-pencil"
                    onClick={this._doReSign}
                    style={{height: parentHeight + "px", minHeight: "30px", verticalAlign: "top"}}
                    title="re-sign"
                    ref={this._setButtonRef}
                /> : null;
                const cb = ws.value && ws.showClearIcon ? <Button
                    key="_clearButton"
                    icon="pi pi-trash"
                    onClick={this._doClearSign}
                    className="p-button-secondary"
                    title="clearing signature"
                    style={{height: parentHeight + "px", minHeight: "30px", verticalAlign: "top"}}
                /> : null;
                const width = (sb || rb) && cb ? 60 : (sb || rb || cb) ? 30 : 0;
                const i = ws.value ? <div className={"text-center " + (!ws.value ? "d-none" : "d-flex")} style={{width: `calc(100% - ${width}px`}}>
                    {image}
                </div> : null;
                const c = ws.value ? null : <div className={"align-items-center justify-content-center bf-cursor-pointer d-flex"}  style={{width: `calc(100% - ${width}px`}} onClick={this._doSign}>
                    <div className="w-100 bf-noBr text-center">{ws.placeholder}</div>
                </div>;
                return (
                    <div className="w-100 d-flex" style={{ height: parentHeight + "px" }}>
                        {i}
                        {c}
                        {sb}
                        {rb}
                        {cb}
                    </div>
                );
            }
        }
    }

    private _callSign(result: boolean) {
        if (result) {
            BlockHandler.load(this.elementId, this.parentDom);
            SignatureHelper.readSign(this.state.wrappedCompState.signAttribute, this._doneReadSign);
        }
    }

    private _doReSign() {
        if (this.state.wrappedCompState.resignConfirm) {
            ExceptionHandler.confirm(ExceptionType.INFO, this.state.wrappedCompState.resignConfirm, this._callSign);
        } else {
            this._callSign(true);
        }
    }

    private _doSign() {
        if (this.state.wrappedCompState.signConfirm) {
            ExceptionHandler.confirm(ExceptionType.INFO, this.state.wrappedCompState.signConfirm, this._callSign);
        } else {
            this._callSign(true);
        }
    }

    private _callClearSign(result: boolean) {
        if (result) {
            this._doneReadSign(null);
        }
    }

    private _doClearSign() {
        if (this.state.wrappedCompState.clearConfirm) {
            ExceptionHandler.confirm(ExceptionType.INFO, this.state.wrappedCompState.clearConfirm, this._callClearSign);
        } else {
            this._callClearSign(true);
        }
    }

    private _doneReadSign(response: any, unload?: boolean) {
        if ((response && response.contents) || unload) {
            BlockHandler.unload(this.elementId);
        }
        const value = response && response.contents ? response.contents : null;
        this.emit(SignatureHelper.makeValue(value, this.state.wrappedCompState.metaInfo, response && response.timestamp ? response.timestamp : null));
        this.setState({
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: value
            }
        });
    }

    private _setRef(ref) {
        if (ref) {
            this._signature = ref;
            this.readyComponent();
        }
    }

    private _setButtonRef(ref) {
        if (ref) {
            this._button = ref.element;
        }
    }
}
